// French overseas (DROM) ZIP prefixes → their own ISO country codes.
const DROM_COUNTRY_BY_PREFIX = {
    971: 'GP',
    972: 'MQ',
    973: 'GF',
    974: 'RE',
    976: 'YT',
};

const DROM_PREFIX_BY_TERRITORY = {
    guadeloupe: '971',
    martinique: '972',
    guyane: '973',
    reunion: '974',
    mayotte: '976',
};

const MAINLAND_TERRITORY = 'france';

export function isFrenchTerritoryScoped(territory) {
    return (
        territory === MAINLAND_TERRITORY ||
        Boolean(DROM_PREFIX_BY_TERRITORY[territory])
    );
}

/** The DROM country code a territory-scoped search is restricted to; undefined for mainland. */
export function getFrenchTerritoryCountry(territory) {
    return DROM_COUNTRY_BY_PREFIX[DROM_PREFIX_BY_TERRITORY[territory]];
}

function getStoreZipPrefix(store) {
    return String(store.zipCode ?? '')
        .replace(/\s/g, '')
        .slice(0, 3);
}

/** Keep only the stores located in the selected territory (mainland excludes every DROM). */
export function filterStoresByFrenchTerritory(stores, territory) {
    const storeList = Object.values(stores ?? {});
    const territoryPrefix = DROM_PREFIX_BY_TERRITORY[territory];

    if (territoryPrefix) {
        return storeList.filter(
            (store) => getStoreZipPrefix(store) === territoryPrefix,
        );
    }

    const dromPrefixes = Object.values(DROM_PREFIX_BY_TERRITORY);
    return storeList.filter(
        (store) => !dromPrefixes.includes(getStoreZipPrefix(store)),
    );
}

/** Whether a geocoder result lies in the selected territory (mainland accepts anything but a DROM). */
export function isGeocodeResultInFrenchTerritory(result, territory) {
    const countryCode = result?.address_components?.find((component) =>
        component.types?.includes('country'),
    )?.short_name;
    const territoryCountry = getFrenchTerritoryCountry(territory);

    if (territoryCountry) {
        return countryCode === territoryCountry;
    }

    return !Object.values(DROM_COUNTRY_BY_PREFIX).includes(countryCode);
}

/** Resolve the geocoder address + country restriction for a French-site search. */
export function resolveFrenchGeocodeParams(rawInput) {
    const input = String(rawInput);

    // Text names stay unrestricted so overseas territories/Monaco aren't excluded.
    if (!/^\d+$/.test(input.trim())) {
        return { address: input, country: undefined };
    }

    return {
        // 97407 doesn't geocode cleanly; use a resolvable nearby address.
        address: input === '97407' ? '97420 Le Port, Réunion' : input,
        country: DROM_COUNTRY_BY_PREFIX[input.slice(0, 3)] ?? 'FR',
    };
}

/** Geocode, retrying unrestricted if a country-restricted French search finds nothing. */
export function geocodeWithFrenchFallback(geocoder, { address, country }, siteHandle, onResult) {
    geocoder.geocode(
        { address, componentRestrictions: { country } },
        (results, status) => {
            const [res] = results ?? [];
            // A restricted search can resolve only to the country centroid (e.g.
            // Monaco 98000 under FR); treat that like no result so the unrestricted
            // retry can find collectivities/Monaco outside the DROM list.
            const onlyCountry =
                res?.address_components?.length === 1 &&
                res.address_components[0].short_name === country;
            const noResult = status !== 'OK' || !results?.length || onlyCountry;

            if (country && siteHandle === 'mgMotorsFrance' && noResult) {
                geocoder.geocode({ address }, onResult);
                return;
            }

            onResult(results, status);
        },
    );
}
